'use client';

import { useState, useCallback, useMemo, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import SwipeCard, { type StatementCard, pickText } from '@/components/SwipeCard';
import WriteOwnSheet from '@/components/WriteOwnSheet';
import CountryBadge from '@/components/CountryBadge';
import VerifyModal from '@/components/VerifyModal';
import ShareModal from '@/components/ShareModal';
import LanguageSelector from '@/components/LanguageSelector';
import ExploreBoard from '@/components/ExploreBoard';
import HelpModal from '@/components/HelpModal';
import { countries } from '@/data/countries';
import { t, tn, countryNameIn } from '@/data/translations';

type Mode = 'explore' | 'focus';

export default function Home() {
  const [mode, setMode] = useState<Mode>('explore');
  const [focusStack, setFocusStack] = useState<StatementCard[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [voteCount, setVoteCount] = useState(0);
  const [showVerify, setShowVerify] = useState(false);
  const [showWrite, setShowWrite] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showLangSelector, setShowLangSelector] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [submittedJustNow, setSubmittedJustNow] = useState<StatementCard | null>(null);
  const [viewMine, setViewMine] = useState(false);
  const [mineCount, setMineCount] = useState(0);
  const [superLikedCard, setSuperLikedCard] = useState<StatementCard | null>(null);
  const [countryCode, setCountryCode] = useState('US');
  const [langCode, setLangCode] = useState('en');
  const [hasVerified] = useState(false);
  const [exitDirection, setExitDirection] = useState<'left' | 'right' | 'up' | null>(null);
  const [showingOriginal, setShowingOriginal] = useState(true);

  // Detect browser language on mount
  useEffect(() => {
    const browserLang = navigator.language.split('-')[0].toLowerCase();
    setLangCode(browserLang);
  }, []);

  // Detect country by IP on mount
  useEffect(() => {
    let cancelled = false;
    fetch('/api/geo')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled) return;
        if (data?.country && countries.some(c => c.code === data.country)) {
          setCountryCode(data.country);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Bootstrap session cookie + count user's existing posts (for the "Yours" badge).
  useEffect(() => {
    let cancelled = false;
    fetch('/api/session', { credentials: 'same-origin' })
      .then(() => fetch('/api/statements?mine=1&limit=100', { credentials: 'same-origin' }))
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled) return;
        if (Array.isArray(data?.statements)) setMineCount(data.statements.length);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const currentCountry = useMemo(() => {
    const fallback = { code: 'US', name: 'United States', flag: '🇺🇸' };
    const base = countries.find(c => c.code === countryCode) || fallback;
    return { ...base, name: countryNameIn(base.code, langCode, true) };
  }, [countryCode, langCode]);

  const currentCard = focusStack[currentIndex];
  const isDone = mode === 'focus' && focusStack.length > 0 && currentIndex >= focusStack.length;

  // Reset which side is showing whenever the card changes — default to UI language if available, else English.
  useEffect(() => {
    if (!currentCard) return;
    const hasTranslation = currentCard.text_english !== null && currentCard.language_code !== 'en';
    const showOriginal = !hasTranslation || currentCard.language_code === langCode;
    setShowingOriginal(showOriginal);
  }, [currentCard, langCode]);

  const userIsAmerican = countryCode === 'US';
  const writeDirection: 'to_america' | 'to_world' = userIsAmerican ? 'to_world' : 'to_america';

  const prefillText = useMemo(() => {
    if (!currentCard) return '';
    const shouldPrefill =
      (!userIsAmerican && currentCard.direction === 'to_america') ||
      (userIsAmerican && currentCard.direction === 'to_world' && currentCard.country_code === 'US');
    if (!shouldPrefill) return '';
    return showingOriginal ? currentCard.text_original : pickText(currentCard, langCode);
  }, [currentCard, userIsAmerican, showingOriginal, langCode]);

  const enterFocus = useCallback((statement: StatementCard, stack: StatementCard[]) => {
    const idx = stack.findIndex(s => s.id === statement.id);
    setFocusStack(stack);
    setCurrentIndex(idx >= 0 ? idx : 0);
    setMode('focus');
  }, []);

  const exitFocus = useCallback(() => {
    setMode('explore');
    setFocusStack([]);
    setCurrentIndex(0);
  }, []);

  const handleVote = useCallback((type: 'like' | 'dislike' | 'super_like') => {
    const card = focusStack[currentIndex];

    const dirMap = { dislike: 'left' as const, like: 'right' as const, super_like: 'up' as const };
    setExitDirection(dirMap[type]);

    setTimeout(() => {
      setCurrentIndex(prev => prev + 1);
      setExitDirection(null);

      const newVoteCount = voteCount + 1;
      setVoteCount(newVoteCount);

      if (type === 'super_like' && card) {
        setSuperLikedCard(card);
        setShowShare(true);
      }

      if (newVoteCount === 5 && !hasVerified) {
        setTimeout(() => setShowVerify(true), 300);
      }
    }, 400);
  }, [currentIndex, voteCount, focusStack, hasVerified]);

  const handleSubmitOwn = useCallback(async (text: string) => {
    try {
      const res = await fetch('/api/statements', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          text,
          language_code: langCode,
          country_code: countryCode,
          direction: writeDirection,
        }),
      });
      if (!res.ok) {
        console.error('submit failed', await res.text());
        return;
      }
      const data = await res.json();
      const flag = countries.find(c => c.code === countryCode)?.flag ?? '🌍';
      const newCard: StatementCard = { ...data.statement, flag };
      setSubmittedJustNow(newCard);
      setMineCount(c => c + 1);
      setShowWrite(false);
      // Drop the user into their own posts view so they can see their submission live.
      setViewMine(true);
    } catch (err) {
      console.error('submit error', err);
    }
  }, [langCode, countryCode, writeDirection]);

  const handleVerifyEmail = useCallback((email: string) => {
    console.log('Verify email:', email);
  }, []);

  const handleChangeLang = useCallback((code: string) => {
    setLangCode(code);
    setShowLangSelector(false);
  }, []);

  return (
    <div className="fixed inset-0 flex flex-col bg-slate-950">
      {/* Top bar */}
      <header className="flex items-center justify-between px-4 pt-3 pb-2 z-30 shrink-0">
        {mode === 'focus' ? (
          <button
            onClick={exitFocus}
            aria-label="Back"
            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-800/80 border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            {viewMine ? (
              <span className="text-sm font-medium">You</span>
            ) : (
              <>
                <span className="text-base">🌍</span>
                <span className="text-sm font-medium" lang={langCode}>{t('all', langCode)}</span>
              </>
            )}
          </button>
        ) : (
          <CountryBadge
            countryCode={countryCode}
            countryName={currentCountry.name}
            flag={currentCountry.flag}
            onChangeCountry={setCountryCode}
            countries={countries}
            langCode={langCode}
          />
        )}

        <div className="flex items-center gap-2">
          {mode === 'explore' && mineCount > 0 && (
            <button
              onClick={() => setViewMine(v => !v)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                viewMine
                  ? 'bg-sky-500/25 border border-sky-400/60 text-sky-200'
                  : 'bg-slate-800/80 border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-600'
              }`}
              aria-pressed={viewMine}
            >
              <span>You</span>
              <span className={`text-[10px] tabular-nums ${viewMine ? 'text-sky-100' : 'text-slate-500'}`}>{mineCount}</span>
            </button>
          )}

          <button
            onClick={() => setShowWrite(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-sky-500/15 border border-sky-500/30 text-sky-300 hover:bg-sky-500/25 transition-colors"
            lang={langCode}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <span className="text-xs font-medium">{t('writeYourOwn', langCode)}</span>
          </button>

          <button
            onClick={() => setShowLangSelector(true)}
            className="px-2.5 py-1.5 rounded-full bg-slate-800/80 border border-slate-700/50 text-xs text-slate-400 font-medium active:text-white transition-colors"
          >
            {langCode.toUpperCase()}
          </button>

          <button
            onClick={() => setShowHelp(true)}
            aria-label="How it works"
            className="w-7 h-7 flex items-center justify-center rounded-full bg-slate-800/80 border border-slate-700/50 text-slate-400 hover:text-white hover:border-slate-600 transition-colors"
          >
            <span className="text-xs font-bold">?</span>
          </button>
        </div>
      </header>

      {/* Title + Hero */}
      <div className="text-center px-4 pb-2 z-20 shrink-0">
        <h1 className="text-sm font-medium tracking-wide text-slate-500">
          <span className="text-sky-400/80">Earth</span>
          <span className="text-slate-600 mx-1">to</span>
          <span className="text-slate-300">America</span>
        </h1>
        <p
          className="text-2xl md:text-3xl font-bold tracking-tight text-white mt-1"
          lang={langCode}
        >
          {t('hey', langCode)}
        </p>
        <p
          className="text-xs md:text-sm text-slate-400 mt-1 max-w-md mx-auto leading-snug"
          lang={langCode}
        >
          {t('tagline', langCode)}
        </p>
      </div>

      {/* Main content area */}
      <div className="flex-1 relative overflow-hidden min-h-0">
        {mode === 'explore' ? (
          <ExploreBoard
            langCode={langCode}
            onSelectStatement={enterFocus}
            viewMine={viewMine}
            submittedJustNow={submittedJustNow}
          />
        ) : (
          <AnimatePresence mode="wait" initial={false}>
            {isDone ? (
              <div key="done" className="absolute inset-0 flex flex-col items-center justify-center px-8 text-center">
                <div className="text-5xl mb-4">🌍</div>
                <h2 className="text-xl font-bold text-white mb-2" lang={langCode}>{t('seenAll', langCode)}</h2>
                <p className="text-slate-400 text-sm mb-6" lang={langCode}>
                  {tn('votedSummary', voteCount, langCode)}
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={exitFocus}
                    className="px-5 py-3 rounded-2xl bg-slate-800 border border-slate-700 text-slate-200 font-medium hover:bg-slate-750 transition-colors"
                    lang={langCode}
                  >
                    ←
                  </button>
                  <button
                    onClick={() => setShowWrite(true)}
                    className="px-6 py-3 rounded-2xl bg-sky-500 text-white font-bold shadow-lg shadow-sky-500/25 active:bg-sky-400 transition-colors"
                  >
                    {t('writeYourOwn', langCode)}
                  </button>
                </div>
              </div>
            ) : currentCard ? (
              <SwipeCard
                key={currentCard.id}
                card={currentCard}
                onVote={handleVote}
                onWriteOwn={() => setShowWrite(true)}
                exitDirection={exitDirection}
                showingOriginal={showingOriginal}
                onToggleSide={() => setShowingOriginal(prev => !prev)}
                langCode={langCode}
              />
            ) : null}
          </AnimatePresence>
        )}
      </div>

      {/* Vote counter */}
      {voteCount > 0 && (
        <div className="fixed top-3 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
          <span className="text-[10px] text-slate-600 bg-slate-900/80 px-2 py-0.5 rounded-full" lang={langCode}>
            {tn('voteCounter', voteCount, langCode)}
          </span>
        </div>
      )}

      {/* Modals */}
      <WriteOwnSheet
        isOpen={showWrite}
        prefillText={prefillText}
        onClose={() => setShowWrite(false)}
        onSubmit={handleSubmitOwn}
        countryName={currentCountry.name}
        countryFlag={currentCountry.flag}
        direction={writeDirection}
        langCode={langCode}
      />

      <VerifyModal
        isOpen={showVerify}
        voteCount={voteCount}
        onSubmit={handleVerifyEmail}
        onSkip={() => setShowVerify(false)}
        langCode={langCode}
      />

      {superLikedCard && (
        <ShareModal
          isOpen={showShare}
          onClose={() => { setShowShare(false); setSuperLikedCard(null); }}
          statementText={superLikedCard.text_english || superLikedCard.text_original}
          countryName={superLikedCard.country_name}
          statementId={superLikedCard.id}
          langCode={langCode}
        />
      )}

      <LanguageSelector
        isOpen={showLangSelector}
        currentCode={langCode}
        onSelect={handleChangeLang}
        onClose={() => setShowLangSelector(false)}
      />

      <HelpModal
        isOpen={showHelp}
        langCode={langCode}
        onClose={() => setShowHelp(false)}
      />
    </div>
  );
}
